import { EMkBookStepName } from '@book/types'
import { ApiRequestConfig, MkApiContext, TMkStep } from '@typescript'

export type RequestConfigResult = {
  stepName: string
  config: ApiRequestConfig
}

const getEndpoint = (context: MkApiContext | undefined, stepName: string | EMkBookStepName): string => {
  switch (stepName) {
    case '*':
      return context &&
      [MkApiContext.MODIFY_TRIP_HUB, MkApiContext.MODIFY_TRIP_DATES, MkApiContext.MODIFY_TRIP_TRAVELER, MkApiContext.MODIFY_TRIP_POINTS_PROTECTION, MkApiContext.MODIFY_TRIP_VIP_UPGRADE].includes(context)
        ? 'modify/start'
        : 'start'
    case EMkBookStepName.COMPLETE_BOOKING:
      return 'complete'
    case EMkBookStepName.MODIFY_HUB:
      return 'modify-hub'
    case EMkBookStepName.CANCEL_TRIP:
      return 'cancel-trip'
    case EMkBookStepName.MODIFY_TRIP_DATES:
    case EMkBookStepName.MODIFY_TRAVELER:
    case EMkBookStepName.MODIFY_REVIEW_PAY:
      return 'modify/step'

    case EMkBookStepName.MODIFY_PROTECTION:
    case EMkBookStepName.MODIFY_COMPLETE:
      return 'modify/complete'
    default:
      return 'step'
  }
}

const getRequestBody = (context: MkApiContext | undefined, stepName: string, body: Record<string, any>): Record<string, any> => {
  let bodyToUse = { ...body } as Record<string, any>
  if (
    context &&
    [
      MkApiContext.MODIFY_TRIP_HUB,
      MkApiContext.MODIFY_TRIP_DATES,
      MkApiContext.MODIFY_TRIP_TRAVELER,
      MkApiContext.MODIFY_TRIP_POINTS_PROTECTION,
      MkApiContext.MODIFY_TRIP_VIP_UPGRADE,
    ].includes(context)
  ) {
    bodyToUse = {
      ...bodyToUse,
      tripId: bodyToUse.tripId,
    }
  }

  switch (stepName) {
    case EMkBookStepName.MODIFY_HUB:
      return { ...bodyToUse }
    case EMkBookStepName.MODIFY_TRIP_DATES:
      return { ...bodyToUse }
    case EMkBookStepName.MODIFY_TRAVELER:
      return {
        ...bodyToUse,
        travelerInfo: {
          ...bodyToUse.travelerInfo,
          name: bodyToUse.travelerInfo?.name || 'N/A',
          email: bodyToUse.travelerInfo?.email || 'N/A',
          phone: bodyToUse.travelerInfo?.phone || 'N/A',
        },
      }
    case EMkBookStepName.MODIFY_PROTECTION:
      return {
        ...bodyToUse,
        tripId: bodyToUse.tripId || 'UNKNOWN_TRIP_ID',
        pointsProtection: {
          optedIn: bodyToUse.pointsProtection?.optedIn ?? false,
          reason: bodyToUse.pointsProtection?.reason || 'N/A',
        },
      }
    case EMkBookStepName.CANCEL_TRIP:
      return {
        ...bodyToUse,
        tripId: bodyToUse.tripId || 'UNKNOWN_TRIP_ID',
        cancellationReason: bodyToUse.cancellationReason || 'No reason provided',
      }
    case EMkBookStepName.MODIFY_COMPLETE:
      return {
        ...bodyToUse,
      }
    case EMkBookStepName.COMPLETE_BOOKING:
      return {
        ...bodyToUse,
      }
    case '*':
      // If context is provided, we might need to handle it differently
      return {
        ...bodyToUse,
        ...(bodyToUse?.suite && {
          unit: {
            // ...body.suite,
            unitId: bodyToUse.suite.unitId || bodyToUse?.vgerResortAvail?.resortOfferings?.[0]?.accomdationClasses?.[0]?.accomodationClassId || bodyToUse.suite.id,
          },
        }),
        ...(bodyToUse?.resort && {
          resort: {
            // ...body.resort,
            resortId: bodyToUse?.resort?.resortNumber,
            productId: bodyToUse?.resort?.productHubId,
          },
        }),
      }
    default:
      return { ...body }
  }
}

export const buildApiRequestConfigFromStep = (
  context: MkApiContext | undefined,
  step: TMkStep | undefined,
  baseUrl: string,
  baseHeaders: Record<string, string>,
  body: Record<string, any>,
): RequestConfigResult => {
  const { name } = step || { name: '*' }

  // Default configuration
  const config: ApiRequestConfig = {
    method: 'POST',
    endpoint: `${baseUrl}/${getEndpoint(context, name)}`,
    headers: { ...baseHeaders },
    body: getRequestBody(context, name, body),
  }

  return { stepName: name, config }
}
